import { AccordionSummary, AccordionDetails, Box, Theme } from '@mui/material';
import { getStyleVariables } from 'styles/vars';
import { useSelector, useDispatch } from 'react-redux';
import { useState } from 'react';
import { GVExpansionPanel } from 'components/common';
import { getDifferencePanelOpen, inspection, getFocusedDifference } from 'store';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { GVTypography } from 'components/lib';
import { makeStyles } from 'tss-react/mui';
import { DifferenceTypeNames } from 'types';
import GVTooltip from 'components/lib/GVToolTip/GVTooltip';
import { createPortal } from 'react-dom';
import { Rnd } from 'react-rnd';
import { FLOATING_PANEL_Z_INDEX, useFloatingPanel } from './useFloatingPanel';

export const useStyles = makeStyles()((theme: Theme) => {
  const styleVariables = getStyleVariables(theme);
  return {
    container: {
      position: 'absolute',
      bottom: 0,
      left: 0,
      zIndex: FLOATING_PANEL_Z_INDEX,
      pointerEvents: 'none',
      '& > *': {
        pointerEvents: 'auto',
      },
    },
    root: {
      backgroundColor: styleVariables.colors.menuLightGrey,
      maxWidth: `calc(100% - ${styleVariables.leftPanel.width})`,
      cursor: 'default',
      '&.drag-handle': {
        cursor: 'grab',
        '&:active': {
          cursor: 'grabbing',
        },
      },
      '& .no-drag': {
        cursor: 'default',
        pointerEvents: 'auto',
      },
    },
    expansionSummaryContent: {
      margin: 0,
    },
    expansionSummaryRoot: {
      margin: theme.spacing(0, 0, 2, 0),
      minHeight: '0px',
      '&.Mui-expanded': {
        minHeight: '0px',
      },
      padding: 0,
      '& .MuiAccordionSummary-expandIconWrapper': {
        cursor: 'pointer',
        pointerEvents: 'auto',
      },
    },
    panelTitle: {
      fontFamily: '"Open Sans"',
      fontSize: '12px',
      lineHeight: '16px',
      fontWeight: 600,
      letterSpacing: 0,
    },
    expandIcon: {
      boxSizing: 'content-box',
      height: '15px',
      width: '15px',
      padding: '4.5px',
      margin: 0,
      borderRadius: '4px',
      color: 'rgba(255, 255, 255, 0.87)',
      '&:hover': {
        backgroundColor: 'rgba(255,255,255,0.1)',
      },
    },
    title: {
      marginBottom: theme.spacing(1),
      fontFamily: '"Open Sans"',
      fontSize: '10px',
      fontWeight: 600,
      lineHeight: '100%',
      letterSpacing: 0,
      color: '#929BB2',
      textTransform: 'uppercase',
    },
    decodedValueContent: {
      padding: '8px',
      borderRadius: '4px',
      backgroundColor: styleVariables.colors.darkGreyBackground,
      width: '100%',
    },
    decodedValueText: {
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      display: 'block',
      width: '100%',
      cursor: 'text',
      fontSize: '14px',
      lineHeight: '22px',
    },
    accordianDetails: {
      flexDirection: 'column',
      padding: theme.spacing(1, 1),
      paddingBottom: '16px',
      flex: '1 1 auto',
      minHeight: 0,
      overflow: 'hidden',
      display: 'flex',
    },
    scrollBar: { ...styleVariables.scrollBar, scrollbarGutter: 'auto' },
    rndContainer: {
      cursor: 'default',
      '& > *': {
        width: '100% !important',
        minWidth: '100% !important',
        flex: '1 1 auto',
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
      },
      '& .drag-handle': {
        cursor: 'grab !important',
        '&:active': {
          cursor: 'grabbing !important',
        },
      },
      '& .no-drag': {
        cursor: 'default !important',
      },
      '&.dragging': {
        cursor: 'grabbing !important',
        '& .drag-handle': {
          cursor: 'grabbing !important',
        },
        '& .no-drag': {
          cursor: 'default !important',
        },
      },
    },
  };
});

export interface DecodedLayoutProps {
  id?: string;
  type: DifferenceTypeNames.Barcode | DifferenceTypeNames.Braille;
  decodedValue: string | JSX.Element;
  metaData: ((container: HTMLDivElement | null) => React.ReactNode) | null;
  /** Hide the "<TYPE> METADATA" caption above the content (the barcode panel renders its own section headers). */
  hideMetaDataTitle?: boolean;
  staticMetaData?: React.ReactNode;
}
const ACCORDION_DETAILS_VERTICAL_PADDING = 24;
const METADATA_TITLE_HEIGHT = 24;
const MIN_METADATA_HEIGHT = 100;

const DecodedLayout = ({
  id,
  type,
  decodedValue,
  metaData,
  hideMetaDataTitle = false,
  staticMetaData,
}: DecodedLayoutProps) => {
  const { classes } = useStyles();
  const [tooltipContainer, setTooltipContainer] = useState<HTMLDivElement | null>(null);
  const panelWidth = type === DifferenceTypeNames.Barcode ? 415 : 367;
  const isOpen = useSelector(getDifferencePanelOpen);
  const currentDifference = useSelector(getFocusedDifference);
  const dispatch = useDispatch();
  const hasMetadata = metaData != null;

  const {
    documentsPanelElement,
    documentsPanelHeight,
    headerRef,
    rndRef,
    position,
    size,
    isDragging,
    isMounted,
    rndHandlers,
    rndStyle,
    rndClassName,
  } = useFloatingPanel({
    panelWidth,
    isOpen,
    dragStyleId: 'decoded-drag-style',
    currentDifferenceId: currentDifference?.id,
    rndContainerClassName: classes.rndContainer,
    onAutoOpen: () => dispatch(inspection.actions.setDifferencePanelOpen(true)),
  });

  // Only a user drag shrinks the content. While closed the panel is docked at the
  // bottom, and counting that offset would clamp the content to its collapsed size
  // in one frame while the panel is still animating down.
  const dragOffsetY = isOpen ? Math.max(0, position.y) : 0;

  const calculateMetadataMaxHeight = () => {
    if (!documentsPanelHeight || !headerRef.current) return undefined;
    const metadataTitleHeight = hideMetaDataTitle ? 0 : METADATA_TITLE_HEIGHT;
    const available =
      documentsPanelHeight -
      headerRef.current.offsetHeight -
      ACCORDION_DETAILS_VERTICAL_PADDING -
      metadataTitleHeight -
      dragOffsetY;
    return `${Math.max(MIN_METADATA_HEIGHT, available)}px`;
  };

  const getAccordionMaxHeight = () => {
    if (!documentsPanelHeight) return undefined;
    return `${Math.max(100, documentsPanelHeight - dragOffsetY)}px`;
  };

  const panelContent = (
    <GVExpansionPanel
      className={classes.root}
      id={id}
      decoded-panel-content="true"
      expanded={isOpen}
      ref={setTooltipContainer}
      TransitionProps={{ timeout: isMounted ? undefined : 0 }}
      sx={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        ...(!isMounted ? { transition: 'none !important' } : {}),
        ...(documentsPanelHeight ? { maxHeight: getAccordionMaxHeight() } : {}),
      }}
    >
      <Box ref={headerRef} flexDirection="column" width="100%" padding="8px" className="drag-handle">
        <AccordionSummary
          style={{ padding: 0 }}
          classes={{ content: classes.expansionSummaryContent }}
          className={classes.expansionSummaryRoot}
          id="BarcodeExpansionPanel"
          expandIcon={
            <ExpandLessIcon
              id="BarcodeButton"
              className={`${classes.expandIcon} no-drag`}
              onClick={(e) => {
                e.stopPropagation();
                dispatch(inspection.actions.setDifferencePanelOpen(!isOpen));
              }}
              style={{ cursor: 'pointer' }}
            />
          }
          onClick={(e) => {
            // Only toggle if clicking on the summary itself, not the icon
            if (!(e.target as HTMLElement).closest('.no-drag')) {
              dispatch(inspection.actions.setDifferencePanelOpen(!isOpen));
            }
          }}
        >
          <GVTypography emphasis="high" variant="subtitle1" className={classes.panelTitle}>
            {type} Details
          </GVTypography>
        </AccordionSummary>
        <GVTypography emphasis="superhigh" variant="body1" className={classes.title}>
          DECODED VALUE
        </GVTypography>
        <Box alignItems="center" className={`${classes.decodedValueContent} no-drag`}>
          {typeof decodedValue === 'string' ? (
            <GVTooltip title={decodedValue} side="bottom" container={tooltipContainer}>
              <span>
                <GVTypography emphasis="superhigh" variant="subtitle2" className={classes.decodedValueText}>
                  {decodedValue}
                </GVTypography>
              </span>
            </GVTooltip>
          ) : (
            <GVTypography emphasis="superhigh" variant="subtitle2">
              {decodedValue}
            </GVTypography>
          )}
        </Box>
        {staticMetaData && <Box className="no-drag">{staticMetaData}</Box>}
      </Box>
      {hasMetadata && (
        <AccordionDetails className={`${classes.accordianDetails} no-drag`}>
          {!hideMetaDataTitle && (
            <GVTypography emphasis="superhigh" variant="body1" className={classes.title}>
              {type.toUpperCase()} METADATA
            </GVTypography>
          )}
          <Box
            flexDirection="column"
            className={classes.scrollBar}
            data-testid="barcode-details-metadata"
            sx={{
              flex: 1,
              minHeight: 0,
              overflowY: 'auto',
              maxHeight: calculateMetadataMaxHeight(),
            }}
          >
            {metaData(tooltipContainer)}
          </Box>
        </AccordionDetails>
      )}
    </GVExpansionPanel>
  );

  const content = (
    <Rnd
      ref={rndRef}
      position={position}
      size={size}
      bounds="window"
      {...rndHandlers}
      style={rndStyle}
      className={rndClassName}
      dragHandleClassName={isDragging ? 'no-drag' : 'drag-handle'}
      enableUserSelectHack={true}
      enableResizing={false}
      cancel=".no-drag"
    >
      {panelContent}
    </Rnd>
  );

  // Render inside documents-panel using portal if available, otherwise render normally
  if (documentsPanelElement) {
    return createPortal(content, documentsPanelElement);
  }

  // Return null if documents-panel not found yet (will render once found)
  return null;
};

export default DecodedLayout;
